import {
  FormProvider,
  FormSubmit,
  Input,
  useFormStatus,
  validations,
} from '@web/components/form';
import { fadeInDown, variantProps } from '@web/features/theme';
import { knownErrors } from '@web/services/tmapi';
import { motion } from 'framer-motion';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { useIsRunningAsIOSApp } from '@web/utils/ios-app';
import { useIsRunningAsAndroidApp } from '@web/utils/android-app';
import { Info } from './info';
import { styles } from './sign-in.styles';
import { useAuth } from './use-auth';

export type SignUpProps = { redirectTo?: string };

export function SignUp({ redirectTo = '/' }: SignUpProps) {
  const router = useRouter();
  const [{ isAuthenticated }, { signUp }] = useAuth();
  const [status, setStatus] = useFormStatus();

  const { error, error_code, magic } = router.query;

  const [isRunningAsIOSApp] = useIsRunningAsIOSApp();
  const [isRunningAsAndroidApp] = useIsRunningAsAndroidApp();

  const onRegistrationSubmit: FormSubmit<{
    email: string;
    password: string;
  }> = async ({ email, password }) => {
    setStatus({ type: 'info', el: 'signing up...' });
    const staySignedIn = isRunningAsIOSApp || isRunningAsAndroidApp;

    return signUp(
      email,
      password,
      redirectTo,
      magic as string,
      staySignedIn,
    ).catch((e) => setStatus({ type: 'error', el: e.message }));
  };

  useEffect(() => {
    if (error || error_code) {
      if (error_code) {
        if (error_code === 'incorrect_password') {
          setStatus({
            type: 'error',
            el: (
              <p>
                An account with this email account exists, but your password is
                incorrect. If you don't remember it, please use the{' '}
                <a href="/password-reset-initiate">password reset option</a>.
              </p>
            ),
          });
        } else {
          setStatus({ type: 'error', el: knownErrors[error_code] || error });
        }
      } else {
        setStatus({ type: 'error', el: error });
      }
    }
  }, [error, error_code]);

  if (isAuthenticated) {
    router.push(redirectTo);
  }

  return (
    <section css={{ maxWidth: '36rem', margin: '5% auto' }}>
      <motion.header variants={fadeInDown} {...variantProps}>
        <h1 css={styles.title}>Welcome to Time Machine!</h1>
        <p css={styles.subtitle}>
          Create an account to love and connect with yourself over time.
        </p>
      </motion.header>

      <FormProvider
        defaultValues={{
          email: '',
          password: '',
          shouldRemember: true,
        }}
        onSubmit={onRegistrationSubmit}
        submitButtonText="Register"
        status={status}
      >
        <Input
          name="email"
          type="email"
          label="Email"
          placeholder="Enter your email"
          autoFocus
          config={{
            required: validations.required,
            pattern: validations.email,
          }}
        />

        <Input
          name="password"
          type="password"
          label="Password"
          placeholder="Enter your password"
          config={{
            required: validations.required,
            minLength: validations.passwordMinLength,
          }}
        />
      </FormProvider>

      <div css={styles.actions}>
        <Link href="/" passHref>
          Go to login
        </Link>
        <Link href="/password-reset-initiate" passHref>
          Forgotten your password?
        </Link>
      </div>

      <Info />
    </section>
  );
}
